import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Send, Image as ImageIcon, Trash2, Users, Bell, BellOff, SmilePlus } from 'lucide-react';
import { t } from '../../utils/i18n';
import { useDmPersona } from '../../context/DmPersonaContext';
import {
  getCelebRoomMessages,
  postCelebRoomMessage,
  postCelebRoomMessagePhoto,
  deleteCelebRoomMessage,
  enterCelebRoom,
  getMyCelebRoom,
  postReaction,
  deleteReaction,
  patchPushPreference,
} from '../../api/celebRoom';
import useBoundedPolling from '../../hooks/useBoundedPolling';
import ObservationStatus from '../../ui/ObservationStatus';
import { CELEB_ROOM_EMOJIS, emojiCharForId } from './emojiMap';

const PAGE_SIZE = 30;
const POLL_INTERVAL_MS = 60_000;
const MAX_CONTENT = 100;

// Message types from celeb-room-api (model.MessageType*): 1 = text, 2 = photo.
const MESSAGE_TYPE_PHOTO = 2;

// The API returns message_type as a number (uint8). Accept the legacy 'photo'
// string too, just in case, so photo bubbles always render the image.
function isPhotoMessage(m) {
  return Number(m?.message_type) === MESSAGE_TYPE_PHOTO || m?.message_type === 'photo';
}

function formatTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function summarizeReactions(reactions, myId) {
  const groups = new Map();
  (reactions || []).forEach((r) => {
    const eid = Number(r.emoji_id);
    const g = groups.get(eid) || { emojiId: eid, count: 0, mine: false };
    g.count += 1;
    if (String(r.fan_user_id) === String(myId)) g.mine = true;
    groups.set(eid, g);
  });
  return Array.from(groups.values());
}

export default function CelebRoomView() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken, userId } = useDmPersona();

  const [messages, setMessages] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState(null);
  const [accessError, setAccessError] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [pickerFor, setPickerFor] = useState(null);

  const messagesMapRef = useRef(new Map());
  const scrollerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [initialEpoch, setInitialEpoch] = useState(0);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const celebUserId = location.state?.celebUserId;

  const rebuild = useCallback(() => {
    const arr = Array.from(messagesMapRef.current.values()).sort(
      (a, b) => a.message_id - b.message_id
    );
    setMessages(arr);
  }, []);

  const applyMessages = useCallback(
    (incoming, deletedIds = []) => {
      const map = messagesMapRef.current;
      (incoming || []).forEach((m) => {
        const id = Number(m.message_id);
        if (!id) return;
        map.set(id, { ...(map.get(id) || {}), ...m, message_id: id });
      });
      (deletedIds || []).forEach((rawId) => {
        const id = Number(rawId);
        const existing = map.get(id);
        if (existing) {
          map.set(id, {
            ...existing,
            is_deleted: true,
            content: null,
            photo_medium_url: null,
            photo_original_url: null,
          });
        }
      });
      rebuild();
    },
    [rebuild]
  );

  const scrollToBottom = useCallback((behavior = 'auto') => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  // Initial load: enter room, resolve ownership, fetch first page.
  useEffect(() => {
    let cancelled = false;
    setLoadingInitial(true);
    setInitialLoaded(false);
    setError(null);
    setAccessError(null);
    setIsOwner(false);
    setPage(0);
    setHasMore(false);
    messagesMapRef.current = new Map();
    setMessages([]);

    if (!accessToken) { setLoadingInitial(false); return () => { cancelled = true; }; }
    (async () => {
      try {
        await enterCelebRoom(roomId, accessToken);
      } catch (e) {
        if ([401, 403].includes(e?.status)) {
          if (!cancelled) { setAccessError(e?.message || t('celebRoom.view.disabled')); setLoadingInitial(false); }
          return;
        }
        // other enter errors are non-fatal for viewing
      }

      try {
        const mine = await getMyCelebRoom(accessToken);
        if (!cancelled && mine && Number(mine.room_id) === Number(roomId)) {
          setIsOwner(true);
        }
      } catch (e) {
        if (e?.status === 401) {
          if (!cancelled) { setAccessError(e?.message || t('celebRoom.view.disabled')); setLoadingInitial(false); }
          return;
        }
        // not a celeb / no room → viewer/fan mode
      }

      try {
        const p = await getCelebRoomMessages(roomId, { page: 0, size: PAGE_SIZE }, accessToken);
        if (cancelled) return;
        setInitialLoaded(true);
        applyMessages(p.items, p.deletedMessageIds);
        setHasMore((p.page + 1) * p.size < p.total);
        setLoadingInitial(false);
        requestAnimationFrame(() => scrollToBottom('auto'));
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || t('celebRoom.view.errors.loadFailed'));
          setLoadingInitial(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [roomId, accessToken, initialEpoch, applyMessages, scrollToBottom]);

  const polling = useBoundedPolling({
    enabled: Boolean(accessToken && initialLoaded && !loadingInitial && !accessError),
    scope: `${roomId}:${accessToken}`,
    interval: POLL_INTERVAL_MS,
    fetch: () => getCelebRoomMessages(roomId, { page: 0, size: PAGE_SIZE }, accessToken),
    onData: p => applyMessages(p.items, p.deletedMessageIds),
    onError: err => setError(err?.message || t('celebRoom.view.errors.loadFailed')),
  });
  const refreshObservation = () => {
    setError(null);
    if (!initialLoaded || accessError) setInitialEpoch(value => value + 1);
    polling.resume();
  };

  const loadOlder = useCallback(async () => {
    if (loadingOlder || !hasMore) return;
    setLoadingOlder(true);
    const el = scrollerRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    const prevTop = el ? el.scrollTop : 0;
    const next = page + 1;
    try {
      const p = await getCelebRoomMessages(roomId, { page: next, size: PAGE_SIZE }, accessToken);
      applyMessages(p.items, p.deletedMessageIds);
      setPage(next);
      setHasMore((p.page + 1) * p.size < p.total);
      requestAnimationFrame(() => {
        if (!scrollerRef.current) return;
        const delta = scrollerRef.current.scrollHeight - prevHeight;
        scrollerRef.current.scrollTop = prevTop + delta;
      });
    } catch (e) {
      setError(e?.message || t('celebRoom.view.errors.loadFailed'));
    } finally {
      setLoadingOlder(false);
    }
  }, [roomId, accessToken, page, hasMore, loadingOlder, applyMessages]);

  const handleSend = useCallback(async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const sent = await postCelebRoomMessage(roomId, content, accessToken);
      if (sent) {
        applyMessages([{ ...sent, is_deleted: false, reactions: [] }]);
        setDraft('');
        requestAnimationFrame(() => scrollToBottom('smooth'));
      }
    } catch (e) {
      setError(e?.message || t('celebRoom.view.errors.sendFailed'));
    } finally {
      setSending(false);
    }
  }, [roomId, accessToken, draft, sending, applyMessages, scrollToBottom]);

  const handlePhoto = useCallback(
    async (file) => {
      if (!file || uploading) return;
      setUploading(true);
      setError(null);
      try {
        const sent = await postCelebRoomMessagePhoto(roomId, file, accessToken);
        if (sent) {
          applyMessages([{ ...sent, is_deleted: false, reactions: [] }]);
          requestAnimationFrame(() => scrollToBottom('smooth'));
        }
      } catch (e) {
        setError(e?.message || t('celebRoom.view.errors.uploadFailed'));
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [roomId, accessToken, uploading, applyMessages, scrollToBottom]
  );

  const handleDelete = useCallback(
    async (messageId) => {
      try {
        await deleteCelebRoomMessage(roomId, messageId, accessToken);
        applyMessages([], [messageId]);
      } catch (e) {
        setError(e?.message || t('celebRoom.view.errors.deleteFailed'));
      }
    },
    [roomId, accessToken, applyMessages]
  );

  const updateReactionsLocal = useCallback(
    (messageId, fn) => {
      const map = messagesMapRef.current;
      const id = Number(messageId);
      const m = map.get(id);
      if (!m) return;
      map.set(id, { ...m, reactions: fn(m.reactions || []) });
      rebuild();
    },
    [rebuild]
  );

  const handleReact = useCallback(
    async (messageId, emojiId) => {
      const myId = String(userId);
      const msg = messagesMapRef.current.get(Number(messageId));
      const mine = (msg?.reactions || []).find((r) => String(r.fan_user_id) === myId);
      try {
        if (mine && Number(mine.emoji_id) === Number(emojiId)) {
          await deleteReaction(roomId, messageId, accessToken);
          updateReactionsLocal(messageId, (rs) =>
            rs.filter((r) => String(r.fan_user_id) !== myId)
          );
        } else {
          await postReaction(roomId, messageId, emojiId, accessToken);
          updateReactionsLocal(messageId, (rs) => {
            const others = rs.filter((r) => String(r.fan_user_id) !== myId);
            return [
              ...others,
              { fan_user_id: myId, emoji_id: emojiId, created_at: new Date().toISOString() },
            ];
          });
        }
        setPickerFor(null);
      } catch (e) {
        setError(e?.message || t('celebRoom.view.errors.reactFailed'));
      }
    },
    [roomId, accessToken, userId, updateReactionsLocal]
  );

  const handlePushToggle = useCallback(async () => {
    const next = !pushEnabled;
    setPushEnabled(next);
    try {
      await patchPushPreference(roomId, next, accessToken);
    } catch (e) {
      setPushEnabled(!next);
      setError(e?.message || t('celebRoom.view.errors.pushFailed'));
    }
  }, [roomId, accessToken, pushEnabled]);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] sm:h-[calc(100vh-4.5rem)] bg-dark-bg">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-dark-border bg-black">
        <button
          type="button"
          onClick={() => navigate('/celeb-room/feed')}
          className="p-1.5 rounded-lg text-dark-text-secondary hover:text-primary hover:bg-dark-surface"
          aria-label={t('common.back')}
        >
          <ArrowLeft size={18} />
        </button>
        <div className="min-w-0 flex-grow">
          <p className="text-sm font-semibold text-white truncate">
            {t('celebRoom.view.roomTitle', { roomId })}
          </p>
          <p className="text-[11px] text-dark-text-muted truncate">
            {isOwner ? t('celebRoom.view.ownerMode') : t('celebRoom.view.fanMode')}
            {celebUserId ? ` · celeb #${celebUserId}` : ''}
          </p>
        </div>
        {!isOwner && (
          <button
            type="button"
            onClick={handlePushToggle}
            className="p-1.5 rounded-lg text-dark-text-secondary hover:text-primary hover:bg-dark-surface"
            aria-label={t('celebRoom.view.pushToggle')}
            title={t('celebRoom.view.pushToggle')}
          >
            {pushEnabled ? <Bell size={18} /> : <BellOff size={18} />}
          </button>
        )}
        <Link
          to={`/celeb-room/r/${roomId}/fans`}
          className="p-1.5 rounded-lg text-dark-text-secondary hover:text-primary hover:bg-dark-surface"
          aria-label={t('celebRoom.view.viewFans')}
          title={t('celebRoom.view.viewFans')}
        >
          <Users size={18} />
        </Link>
      </div>

      <ObservationStatus {...polling} onResume={refreshObservation} disabled={!accessToken || loadingInitial} />
      {/* Message list */}
      <div ref={scrollerRef} className="flex-grow overflow-y-auto px-3 sm:px-6 py-4">
        {loadingInitial ? (
          <div className="text-center py-10 text-dark-text-secondary text-sm">
            {t('common.loading')}
          </div>
        ) : accessError ? (
          <div className="mt-4 text-center text-sm text-red-300 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
            {accessError}
          </div>
        ) : (
          <>
            {hasMore && (
              <div className="text-center py-2">
                <button
                  type="button"
                  onClick={loadOlder}
                  disabled={loadingOlder}
                  className="px-3 py-1.5 rounded-lg bg-dark-surface border border-dark-border text-dark-text-secondary text-xs hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-50"
                >
                  {loadingOlder ? t('common.loading') : t('celebRoom.view.loadOlder')}
                </button>
              </div>
            )}
            {messages.length === 0 ? (
              <p className="text-center text-xs text-dark-text-muted py-8">
                {t('celebRoom.view.empty')}
              </p>
            ) : (
              <ul className="space-y-3">
                {messages.map((m) => {
                  const mineSide = isOwner; // messages are always authored by the celeb
                  const reactionGroups = summarizeReactions(m.reactions, userId);
                  return (
                    <li
                      key={m.message_id}
                      className={`flex ${mineSide ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] sm:max-w-[75%] flex flex-col ${mineSide ? 'items-end' : 'items-start'}`}>
                        <div
                          className={`relative group px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                            mineSide
                              ? 'bg-primary text-black'
                              : 'bg-dark-card border border-dark-border text-white'
                          }`}
                        >
                          {m.is_deleted ? (
                            <span className="italic opacity-70">{t('celebRoom.view.deletedMessage')}</span>
                          ) : isPhotoMessage(m) ? (
                            <a
                              href={m.photo_original_url || m.photo_medium_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={m.photo_medium_url}
                                alt=""
                                className="rounded-lg max-w-[240px] max-h-[320px] object-cover"
                              />
                            </a>
                          ) : (
                            m.content
                          )}
                        </div>

                        {/* meta row */}
                        <div className="mt-0.5 flex items-center gap-2 text-[10px] text-dark-text-muted">
                          <span>{formatTime(m.created_at)}</span>
                          <span className="opacity-70">#{m.message_id}</span>
                          {isOwner && !m.is_deleted && (
                            <button
                              type="button"
                              onClick={() => handleDelete(m.message_id)}
                              className="inline-flex items-center gap-1 text-red-300 hover:text-red-200"
                            >
                              <Trash2 size={11} />
                              {t('celebRoom.view.delete')}
                            </button>
                          )}
                          {!isOwner && !m.is_deleted && (
                            <button
                              type="button"
                              onClick={() =>
                                setPickerFor((prev) => (prev === m.message_id ? null : m.message_id))
                              }
                              className="inline-flex items-center gap-1 hover:text-primary"
                            >
                              <SmilePlus size={12} />
                              {t('celebRoom.view.react')}
                            </button>
                          )}
                        </div>

                        {/* emoji picker */}
                        {!isOwner && pickerFor === m.message_id && (
                          <div className="mt-1 flex flex-wrap gap-1 p-1.5 rounded-lg bg-dark-card border border-dark-border">
                            {CELEB_ROOM_EMOJIS.map((e) => (
                              <button
                                key={e.id}
                                type="button"
                                onClick={() => handleReact(m.message_id, e.id)}
                                className="w-7 h-7 rounded hover:bg-dark-surface text-base leading-none"
                              >
                                {e.char}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* reaction chips */}
                        {reactionGroups.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {reactionGroups.map((g) => (
                              <button
                                key={g.emojiId}
                                type="button"
                                disabled={isOwner}
                                onClick={() => !isOwner && handleReact(m.message_id, g.emojiId)}
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border ${
                                  g.mine
                                    ? 'bg-primary/15 border-primary/40 text-primary'
                                    : 'bg-dark-surface border-dark-border text-dark-text-secondary'
                                } ${isOwner ? 'cursor-default' : 'hover:border-primary/40'}`}
                              >
                                <span className="leading-none">{emojiCharForId(g.emojiId)}</span>
                                <span>{g.count}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
        {error && !loadingInitial && (
          <p className="mt-4 text-center text-xs text-red-300 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
            {error}
          </p>
        )}
      </div>

      {/* Composer (owner only) */}
      {isOwner && !accessError && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex items-end gap-2 px-3 sm:px-6 py-3 border-t border-dark-border bg-black"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            className="hidden"
            onChange={(e) => handlePhoto(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="p-2 rounded-xl text-dark-text-secondary hover:text-primary hover:bg-dark-surface disabled:opacity-50"
            aria-label={t('celebRoom.view.uploadPhoto')}
            title={t('celebRoom.view.uploadPhoto')}
          >
            <ImageIcon size={18} />
          </button>
          <div className="flex-grow relative">
            <textarea
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_CONTENT))}
              placeholder={t('celebRoom.view.inputPlaceholder')}
              className="w-full bg-dark-surface border border-dark-border rounded-xl px-3 py-2 pr-12 text-sm text-white placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none max-h-32"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              maxLength={MAX_CONTENT}
            />
            <span className="absolute right-3 bottom-2 text-[10px] text-dark-text-muted">
              {draft.length}/{MAX_CONTENT}
            </span>
          </div>
          <button
            type="submit"
            disabled={sending || uploading || !draft.trim()}
            className="inline-flex items-center gap-1 bg-primary text-black font-bold px-3 sm:px-4 py-2 rounded-xl disabled:opacity-50 hover:bg-primary/90"
            aria-label={t('celebRoom.view.send')}
          >
            <Send size={16} />
            <span className="hidden sm:inline">
              {sending || uploading ? t('celebRoom.view.sending') : t('celebRoom.view.send')}
            </span>
          </button>
        </form>
      )}

      {!isOwner && !accessError && (
        <div className="px-4 sm:px-6 py-2 text-center text-[11px] text-dark-text-muted border-t border-dark-border bg-black">
          {t('celebRoom.view.fanHint')}
        </div>
      )}
    </div>
  );
}
